<template>
    <table class="table-bordered">
        <caption v-if="noDataText" class="sr-only">
            {{
                noDataText
            }}
        </caption>
        <caption v-else class="sr-only">
            {{
                $t('facture.suivi.tableau.commune')
            }}
        </caption>
        <thead :class="notSticky ? 'not-sticky' : 'sticky'">
            <tr>
                <DataTableHeader
                    v-for="header in tableHeader"
                    :key="header.label"
                    :header="header"
                    class="column"
                    @sort="emitSort($event)"
                ></DataTableHeader>
            </tr>
        </thead>
        <tbody v-if="tableData.length > 0">
            <tr
                v-for="items in tableData"
                :key="items.id"
                :class="isPointer(items) ? 'cursor-pointer-on-pdf-available' : ''"
                class="pointer"
                tabindex="0"
                @click="emitSelect(items)"
                @keydown.enter="emitSelect(items)"
                @keydown.space.prevent="emitSelect(items)"
            >
                <td
                    v-for="(item, i) in getVisibleItems(Object.entries(items))"
                    :key="item[0]"
                    :headers="tableHeader[i] ? `th-${tableHeader[i].sortData || tableHeader[i].label}` : undefined"
                    :style="getStyle(tableHeader[i])"
                >
                    <router-link
                        v-if="item[0] === 'id'"
                        :aria-label="`${$t('list.view.detail')} ${item[1].label}`"
                        :to="{ name: item[1].pageName, params: { id: item[1].label, value: item[1].value } }"
                    >
                        {{ item[1].label }}
                    </router-link>

                    <div v-else-if="item[0] === 'emails'">
                        <ul v-if="Array.isArray(item[1]) && item[1].length" class="email-list">
                            <li v-for="(mail, idx) in item[1]" :key="idx">
                                <span class="no-padding email-chip">{{ mail }}</span>
                            </li>
                        </ul>
                    </div>

                    <div v-else-if="item[0] === 'actif'" @click.stop @keydown.space.stop>
                        <Switch :active="!!item[1]" @toggle="$emit('toggleActif', items)" />
                    </div>

                    <div v-else-if="item[0] === 'actions'" class="actions" @keydown.space.stop>
                        <button
                            :aria-label="$t('commons.edit')"
                            class="btn-icon"
                            type="button"
                            @click.stop="$emit('edit', items)"
                        >
                            <img :src="editIcon" alt="" aria-hidden="true" class="action-icon" />
                        </button>
                        <button
                            :aria-label="$t('commons.delete')"
                            class="btn-icon"
                            type="button"
                            @click.stop="$emit('delete', items)"
                        >
                            <img :src="deleteIcon" alt="" aria-hidden="true" class="action-icon" />
                        </button>
                    </div>
                    <div v-else-if="item[0] === 'idReclamation'">
                        <p class="fake-link">{{ item[1] }}</p>
                    </div>
                    <div v-else-if="item[0] === 'devisId'">
                        <p class="fake-link">{{ item[1].label }}</p>
                    </div>
                    <div v-else-if="item[0] === 'statut'">
                        <StatusTag :status="item[1]" />
                    </div>
                    <div v-else-if="item[0] === 'statutFournisseur'">
                        <StatusFournisseurTag :status="item[1]" />
                    </div>
                    <div v-else-if="item[0] === 'uniqueDocumentId' && item[1] !== ''" class="checboxContainer">
                        <span
                            :aria-checked="isChecked(item[1])"
                            :class="getCheckedStyle(item[1])"
                            role="checkbox"
                            tabindex="0"
                            @keydown.enter.stop="emitToggle(item[1])"
                            @keydown.space.prevent.stop="emitToggle(item[1])"
                        ></span>
                    </div>
                    <div v-else-if="item[0] === 'pdfExist' && item[1] === true">
                        <button
                            :aria-label="$t('aria.telecharger.pdf')"
                            class="btn-icon"
                            type="button"
                            @click.stop="emitDownload(items, $event)"
                        >
                            <img :src="pdfIcon" alt="" aria-hidden="true" class="pdf-icon" />
                        </button>
                    </div>
                    <div v-else-if="item[0] === 'pdfExist' && item[1] === false">
                        <span></span>
                    </div>
                    <div v-else-if="item[0] === 'facturee'">
                        <img v-if="item[1] === true" :src="checkBoxIcon" alt="" aria-hidden="true" class="pdf-icon" />
                        <span v-else></span>
                    </div>

                    <Popper
                        v-else-if="item[0] === 'site'"
                        :content="item[1]"
                        arrow
                        hover
                        offsetDistance="0"
                        placement="top"
                    >
                        <div class="no-padding">
                            <span class="no-padding">{{ item[1] }} </span>
                        </div>
                    </Popper>

                    <div v-else-if="item[0] === 'libelleEquipement' || item[0] === 'equipment'">
                        <template v-if="item[1] && typeof item[1] === 'object'">
                            <span>{{ item[1].libelle ?? '' }}</span>
                            <span v-if="item[1].phase" class="item-info">{{ item[1].phase }}</span>
                        </template>
                        <span v-else>{{ item[1] }}</span>
                    </div>
                    <!-- style white space pre : permet le saut de ligne (AdministrativePage > const adresseSite... \n)-->
                    <span v-else :class="{ 'white-space-pre': item[0] === 'adresse' || item[0] === 'description' }">{{
                        item[1]
                    }}</span>
                </td>
            </tr>
            <tr v-if="isLoading && count !== tableData.length">
                <td :colspan="Object.entries(tableHeader).length" class="noDataTd">
                    {{ $t('search.isLoading') }}
                </td>
            </tr>
        </tbody>
        <tbody v-else-if="isLoading">
            <tr>
                <td :colspan="Object.entries(tableHeader).length" class="noDataTd">
                    {{ $t('search.isLoading') }}
                </td>
            </tr>
        </tbody>
        <tbody v-else>
            <tr>
                <td :colspan="Object.entries(tableHeader).length" class="noDataTd">
                    {{ noDataText ? noDataText : $t('search.notFound') }}
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script lang="ts">
import { SortType } from '@/models/SortType';
import { TableHeader } from '@/models/TableHeader';
import { defineComponent, PropType } from 'vue';
import StatusTag from './StatusTag.vue';
import DataTableHeader from '@/components/ui-components/DataTableHeader.vue';
import StatusFournisseurTag from '@/components/ui-components/StatusTagFournisseur.vue';
import Switch from '@/components/ui-components/Switch.vue';

export default defineComponent({
    name: 'DataTable',
    components: {
        StatusFournisseurTag,
        StatusTag,
        DataTableHeader,
        Switch,
    },
    setup() {
        return {
            SortType,
        };
    },
    props: {
        tableData: { type: Array, required: true },
        tableHeader: { type: Array as PropType<TableHeader[]>, required: true },
        documentListToDownload: { type: Array, required: false },
        noDataText: { type: String, required: false },
        notSticky: { type: Boolean, required: false },
        isLoading: { type: Boolean, required: false },
        count: { type: Number, required: false },
        stopPropaOnPdf: { type: Boolean, required: false },
    },
    methods: {
        emitSort(sortEvent: { sortData: string; sortStatus: SortType }) {
            this.$emit('sort', sortEvent);
        },
        emitSelect(item: any) {
            this.$emit('select', item);
        },
        emitDownload(item: any, event: Event) {
            if (this.stopPropaOnPdf) {
                event.stopPropagation();
                this.$emit('downloadPdf', item);
            }
        },
        getVisibleItems(items: [string, any][]) {
            return items.filter(
                ([key]) =>
                    !key.includes('invisible') &&
                    key !== 'idMasque' &&
                    key !== 'idDocExterne' &&
                    key !== 'idVersionExterne'
            );
        },
        emitToggle(itemId: number) {
            this.$emit('toggle', itemId);
        },
        isChecked(itemId: number): boolean {
            return !!this.documentListToDownload?.find(element => element === itemId);
        },
        getStyle(tableHeader: TableHeader) {
            let css = '';
            if (tableHeader && tableHeader.width) {
                css += 'width: ' + tableHeader.width + ';';
            }
            if (tableHeader && tableHeader.textalign) {
                css += 'text-align: ' + tableHeader.textalign + ';';
            }
            return css;
        },
        getCheckedStyle(itemId: number) {
            let css = 'checkmark';
            if (this.documentListToDownload?.find(element => element === itemId)) {
                css += ' checked';
            }
            return css;
        },
        isPointer(items: any): boolean {
            return items.facturee;
        },
    },
    computed: {
        pdfIcon() {
            return require('@/assets/img/pdf-icon.svg');
        },
        checkBoxIcon() {
            return require('@/assets/img/valid2.svg');
        },
        editIcon() {
            return require('@/assets/img/edit.svg');
        },
        deleteIcon() {
            return require('@/assets/img/delete.svg');
        },
    },
});
</script>

<style scoped>
table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    border-right: 1px solid var(--medium-dark-grey);
    border-left: 1px solid var(--medium-dark-grey);
    border-bottom: 1px solid var(--medium-dark-grey);
    text-align: left;
    font-variant-numeric: lining-nums;
}

.actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}
.action-icon {
    width: 35px;
    height: 35px;
}

.email-list {
    margin: 0;
    padding-left: 0;
}
.email-list li {
    list-style: none;
    line-height: 1.3;
    white-space: nowrap;
    word-break: normal;
    overflow-wrap: normal;
    overflow: hidden;
    text-overflow: ellipsis;
}

table thead {
    top: 0;
    font-size: 14px;
    color: var(--dark-grey);
    font-family: 'Raleway Semi Bold', sans-serif !important;
    font-variant-numeric: lining-nums;
}

.sticky {
    position: sticky;
}

.not-sticky {
    position: static;
}

table thead th {
    top: 0;
    border-bottom: 1px solid var(--medium-dark-grey);
    border-top: 1px solid var(--medium-dark-grey);
    background-color: white;
    height: 55px;
    white-space: pre-line;
}

table tr td {
    border-bottom: 1px solid var(--medium-dark-grey);
    height: 55px;
    padding: 0 15px;
    overflow: hidden;
}

table tr td span {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: keep-all;
    overflow: hidden;
}

table thead tr:first-child {
    border-top: none;
}

table tr * {
    box-sizing: border-box;
    padding: 0 5px;
}

.pointer {
    cursor: pointer;
}
.cursor-pointer-on-pdf-available {
    cursor: pointer !important;
}
.noDataTd {
    text-align: center;
}
.fake-link {
    text-decoration: underline;
    color: -webkit-link;
    cursor: pointer;
}
.pdf-icon {
    height: 30px;
}
.btn-icon {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
}
.item-info {
    color: var(--medium-dark-grey);
    font-size: var(--font-size-small);
}
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
}
.checboxContainer {
    display: flex;
    align-items: center;
    flex-direction: column;
    z-index: 2;
}
.checkmark {
    height: 18px;
    width: 18px;
    border: 2px solid #666666;
    border-radius: 3px;
    background-color: #eee;
}

.checkmark:after {
    content: '';
    position: absolute;
    display: none;
}

.checked {
    background-color: #2196f3;
}

span.checked:after {
    display: block;
    position: relative;
}

.checkmark:after {
    width: 3px;
    height: 8px;
    border: solid white;
    border-width: 0 3px 3px 0;
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
}
:deep(.popper) {
    --popper-theme-background-color: var(--background);
    --popper-theme-border-radius: 5px;
    --popper-theme-padding: 5px;
}

:deep(.popper #arrow::before) {
    background: var(--background);
}

.no-padding {
    padding: 0;
}
.white-space-pre {
    white-space: pre-wrap;
}
.table-bordered td,
.table-bordered th {
    border-left: 1px solid #f0f0f2;
}
</style>
